export const ResourceKind = {
  LEARNING_PATH: "LEARNING_PATH",
  VIDEO: "VIDEO",
  REFERENCE: "REFERENCE",
} as const;

export interface StudyResource {
  readonly title: string;
  readonly description: string;
  readonly url: string;
  readonly kind: (typeof ResourceKind)[keyof typeof ResourceKind];
  readonly sequence: number;
}

export const STUDY_RESOURCES = [
  {
    title: "Understand how Cloudflare works",
    description: "Start with the request path, reverse proxy, DNS, edge, and origin.",
    url: "https://developers.cloudflare.com/videos/how-cf-works/",
    kind: ResourceKind.VIDEO,
    sequence: 1,
  },
  {
    title: "Application Security learning path",
    description: "The public source-of-truth sequence for account security, default protections, WAF, rate limiting, lists, and Security Center.",
    url: "https://developers.cloudflare.com/learning-paths/application-security/",
    kind: ResourceKind.LEARNING_PATH,
    sequence: 2,
  },
  {
    title: "Application Security: get started",
    description: "A practical walkthrough of managed rules, analytics, custom rules, and false-positive handling.",
    url: "https://developers.cloudflare.com/videos/app-sec-get-started/",
    kind: ResourceKind.VIDEO,
    sequence: 3,
  },
  {
    title: "Application Security dashboard walkthrough",
    description: "Learn where traffic, events, endpoints, schema validation, client-side assets, rules, and settings live.",
    url: "https://developers.cloudflare.com/videos/app-sec-dashboard/",
    kind: ResourceKind.VIDEO,
    sequence: 4,
  },
  {
    title: "Secure application delivery architecture",
    description: "Trace DNS, reverse proxying, origin connectivity, Access, WAF, caching, and application delivery as one architecture.",
    url: "https://developers.cloudflare.com/reference-architecture/design-guides/secure-application-delivery/",
    kind: ResourceKind.REFERENCE,
    sequence: 5,
  },
  {
    title: "SASE: evolution of corporate networks",
    description: "Build the mental model for Cloudflare One, SASE, legacy network limitations, and the connectivity cloud.",
    url: "https://developers.cloudflare.com/videos/sase-1-evolution-corp-networks/",
    kind: ResourceKind.VIDEO,
    sequence: 6,
  },
  {
    title: "Secure Internet traffic and SaaS apps",
    description: "The public learning path for Gateway, DNS/network/HTTP policy, TLS inspection, DLP, Browser Isolation, egress, and SaaS security.",
    url: "https://developers.cloudflare.com/learning-paths/secure-internet-traffic/",
    kind: ResourceKind.LEARNING_PATH,
    sequence: 7,
  },
  {
    title: "Replace your VPN",
    description: "Hands-on progression through identity, Tunnel or Mesh, Cloudflare One Client, routing, policy, and troubleshooting.",
    url: "https://developers.cloudflare.com/learning-paths/replace-vpn/",
    kind: ResourceKind.LEARNING_PATH,
    sequence: 8,
  },
  {
    title: "Set up Cloudflare Tunnel",
    description: "Create an outbound connector from a private environment to Cloudflare.",
    url: "https://developers.cloudflare.com/videos/set-up-cf-tunnel/",
    kind: ResourceKind.VIDEO,
    sequence: 9,
  },
  {
    title: "Protect a tunnel with Access",
    description: "Add identity-aware policy in front of a tunneled application.",
    url: "https://developers.cloudflare.com/videos/set-up-access-policies/",
    kind: ResourceKind.VIDEO,
    sequence: 10,
  },
  {
    title: "Cloudflare One Client fundamentals",
    description: "Learn corporate versus consumer behavior, modes, profiles, split tunneling, status, and diagnostic basics.",
    url: "https://developers.cloudflare.com/videos/warp-1-basics/",
    kind: ResourceKind.VIDEO,
    sequence: 11,
  },
  {
    title: "Cloudflare security architecture",
    description: "Connect application, network, identity, data, and edge security products to attacks and deployment patterns.",
    url: "https://developers.cloudflare.com/reference-architecture/architectures/security/",
    kind: ResourceKind.REFERENCE,
    sequence: 12,
  },
] as const satisfies readonly StudyResource[];
